import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronRight, Globe } from 'lucide-react';
import { navLinks } from '../data/navigation';

const Header = ({ lang = 'en', toggleLang }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isAr = lang === 'ar';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLanguageChange = () => {
    if (typeof toggleLang === 'function') {
      toggleLang();
      setIsOpen(false);
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-black/80 backdrop-blur-md border-b border-white/10 shadow-lg'
            : 'bg-transparent'
        }`}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        <div className='section-padding py-3 md:py-4'>
          <div className='flex items-center justify-between'>
            {/* Logo Section */}
            <Link
              to='/'
              aria-label='Super Classy Home'
              className={`flex items-center group ${isAr ? 'space-x-reverse space-x-3' : 'space-x-3'}`}
            >
              <div className='relative'>
                <img
                  src='/assets/images/logo/logo.png'
                  alt='Super Classy Logo'
                  /* ✅ Performance Fix: Explicit Dimensions for CLS */
                  width='48'
                  height='48'
                  className='h-10 w-10 md:h-12 md:w-12 object-contain transition-transform duration-300 group-hover:scale-110'
                  loading='eager'
                />
              </div>
              <div className='flex flex-col'>
                <span className='text-xl md:text-2xl font-black tracking-tight text-gradient'>
                  Super Classy
                </span>
                <span className='text-[8px] md:text-[10px] font-bold uppercase tracking-widest text-gray-400'>
                  {isAr
                    ? 'غسيل سيارات متنقل بالكويت'
                    : 'Mobile Car Wash in Kuwait'}
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav
              className={`hidden md:flex items-center ${isAr ? 'space-x-reverse space-x-8' : 'space-x-8'}`}
            >
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-1 py-2 text-sm font-bold transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {isAr ? link.nameAr : link.name}
                  {location.pathname === link.path && (
                    <motion.div
                      layoutId='underline'
                      className='absolute bottom-0 left-0 right-0 h-0.5 premium-gradient rounded-full'
                    />
                  )}
                </Link>
              ))}

              {/* ✅ Accessibility Fix: Added aria-label */}
              <button
                onClick={handleLanguageChange}
                aria-label={
                  isAr ? 'Switch to English' : 'تغيير اللغة إلى العربية'
                }
                className='flex items-center gap-2 px-4 py-2 premium-gradient text-white rounded-full font-bold shadow-lg hover:scale-105 active:scale-95 transition-all duration-300'
              >
                <Globe className='h-4 w-4' aria-hidden='true' />
                <span className='text-xs'>{isAr ? 'English' : 'عربي'}</span>
              </button>
            </nav>

            {/* Mobile Toggle ✅ Accessibility Fix: Added aria-label */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? 'Close Menu' : 'Open Menu'}
              className='md:hidden p-2 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md text-white'
            >
              {isOpen ? (
                <X className='h-6 w-6' />
              ) : (
                <Menu className='h-6 w-6' />
              )}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: isAr ? '-100%' : '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isAr ? '-100%' : '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className='fixed inset-0 z-[60] md:hidden bg-black/95 backdrop-blur-xl'
            dir={isAr ? 'rtl' : 'ltr'}
          >
            <div className='flex flex-col h-full p-8'>
              <div className='flex items-center justify-between mb-12'>
                <span className='text-2xl font-black text-gradient'>
                  Super Classy
                </span>
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label='Close Menu'
                  className='p-2 rounded-full bg-white/10 text-white'
                >
                  <X className='h-6 w-6' />
                </button>
              </div>

              <nav className='flex-1 space-y-4'>
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center justify-between p-4 rounded-2xl font-bold transition-all ${
                      location.pathname === link.path
                        ? 'bg-royal-purple-500 text-white shadow-lg'
                        : 'bg-white/5 text-gray-300'
                    }`}
                  >
                    <span>{isAr ? link.nameAr : link.name}</span>
                    <ChevronRight
                      className={`h-5 w-5 ${isAr ? 'rotate-180' : ''}`}
                      aria-hidden='true'
                    />
                  </Link>
                ))}
              </nav>

              <button
                onClick={handleLanguageChange}
                aria-label='Toggle Language'
                className='w-full mt-6 py-4 premium-gradient text-white rounded-2xl font-black text-lg flex items-center justify-center gap-3 active:scale-95 transition-transform'
              >
                <Globe className='h-6 w-6' aria-hidden='true' />
                {isAr ? 'English' : 'تغيير إلى العربية'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
